class _OrganizationsAPI {
  constructor() {
    this.perPage = 'all';
    this.versionPrefix = '/v2';
    this.url = `${this.versionPrefix}/organizations/`;
  }

  /**
   * Initialize.
   * TEMPORARY:
   *    The whole axios / auth should be replaced by proper install & usage.
   * @param {object} $axios - @nuxtjs/axios instance
   */
  init($axios, $config) {
    this.API = $axios;
    this.config = $config;
  }

  /**
   * Fetch Mission Medias
   * @param {string} searchString - The search string
   * @return {array} - Array of organizations
   */
  async searchOrganizations(searchQuery, meta = {}) {
    const perPage = meta && meta.perPage ? meta.perPage : this.perPage;
    // Assemble request params
    const params = [
      `per_page=${perPage}`,
      `filter[name]=${searchQuery}`,
    ];
    const requestURI = `${this.url}?${params.join('&')}`;
    try {
        const { status, data } = await this.API.get(requestURI);
        return Promise.resolve({ status, data: data.data, meta: data.meta });
    } catch ({ response, code }) {
        const res = {
          data: response ? response.data : null,
          status: response ? response.status : null,
        };
        return Promise.reject(res);
    }
  }

  async getOrganization(organizationId) {
    // Assemble request params
    const params = [
      'include=billingInfo.address,identifications,paymentInfo',
    ];
    const requestURI = `${this.url}${organizationId}?${params.join('&')}`;
    try {
        const { status, data } = await this.API.get(requestURI);
        return Promise.resolve({ status, data: data.data, meta: data.meta });
    } catch ({ response, code }) {
        const res = {
          data: response ? response.data : null,
          status: response ? response.status : null,
        };
        return Promise.reject(res);
    }
  }

  /**
   * Get documents for an organization
   * @param organizationId
   * @returns {Promise<unknown>}
   */
  async getOrganizationDocument(organizationId) {
    const params = [
      'include=organization.addresses',
    ];
    const requestURI = `${this.url}${organizationId}/documents?${params.join('&')}`;
    try {
      const documents = await this.API.get(requestURI);
      return Promise.resolve(documents);
    } catch (response) {
      return Promise.reject(response);
    }
  }

  /**
   * Upload a document for an organization
   * @param organizationId
   * @param file
   * @param id
   * @returns {Promise<unknown>}
   */
  async uploadOrganizationDocument(organizationId, file, id) {
    const formData = new FormData();
    formData.append('document', file);
    const requestURI = `${this.url}${organizationId}/documents/${id}/upload`;
    try {
      await this.API.post(
        requestURI,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        },
);
      return Promise.resolve(true);
    } catch (response) {
      return Promise.reject(response);
    }
  }

  /**
   * Fetch a single Organization possible legal statuses
   * @param {string} organizationId - The Organization Id
   */
   async fetchOrganizationPossibleLegalStatuses(organizationId) {
    const requestURL = `${this.url}${organizationId}/legal-statuses`;
    try {
      const response = await this.API.get(requestURL);
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Create Organization billing infos
   * @param {string} organizationId - The Organization Id
   * @param {string} attributes - The Organization billing infos
   */
   async createOrganizationBillingInfos(payload) {
    const requestURI = `${this.url}${payload.organizationId}/billing`;
    try {
      const response = await this.API.post(requestURI, {
        data: {
          type: 'organization_billing_info',
          attributes: payload.attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update Organization billing infos
   * @param {string} organizationId - The Organization Id
   * @param {string} attributes - The Organization billing infos
   */
    async updateOrganizationBillingInfos(payload) {
    const requestURI = `${this.url}${payload.organizationId}/billing`;
    try {
      const response = await this.API.patch(requestURI, {
        data: {
          id: payload.billingId,
          type: 'organization_billing_info',
          attributes: payload.attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update the billing address
   * @param organizationId
   * @param address
   * @returns {Promise<unknown>}
   */
  async updateOrganizationBillingAddress(organizationId, address) {
    const requestURI = `${this.url}${organizationId}/billing/address`;
    try {
      const response = await this.API.patch(requestURI, {
        data: {
          type: 'address',
          attributes: address,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Create the billing info address
   * (the first time => see document-revamp/index)
   * @param organizationId
   * @param address
   * @param config
   * @returns {Promise<unknown>}
   */
  async createOrganizationBillingAddress(organizationId, address) {
    const requestURI = `${this.url}${organizationId}/billing/address`;
    try {
      const response = await this.API.post(requestURI, {
        data: {
          type: 'address',
          attributes: address?.attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async deleteOrganizationBillingAddress(organizationId) {
    const requestURI = `${this.url}${organizationId}/billing/address`;
    try {
      const response = await this.API.delete(requestURI);
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Create Organization identification
   * @param {string} organizationId - The Organization Id
   * @param {string} attributes - The Organization identification
   */
   async createOrganizationIdentification(payload) {
    const requestURI = `${this.url}${payload.organizationId}/identifications`;
    try {
      const response = await this.API.post(requestURI, {
        data: {
          type: 'organization_identification',
          attributes: payload.attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update Organization identification
   * @param {string} organizationId - The Organization Id
   * @param {string} attributes - The Organization identification
   */
    async updateOrganizationIdentification(payload) {
    const requestURI = `${this.url}${payload.organizationId}/identifications/${payload.identificationId}`;
    try {
      const response = await this.API.patch(requestURI, {
        data: {
          id: payload.identificationId,
          type: 'organization_identification',
          attributes: payload.attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getMailTemplateCategories() {
    const requestURI = `${this.versionPrefix}/mails/templates/categories`;
    try {
      const response = await this.API.get(requestURI);
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async getMailTemplateUnsubscriptions(organizationId) {
    const params = [
      'include=mailTemplateUnsubscriptions',
    ];
    const requestURI = `${this.url}${organizationId}?${params.join('&')}`;
    try {
      const { status, data } = await this.API.get(requestURI);
      return Promise.resolve({ status, data: data.data, meta: data.meta });
    } catch ({ response, code }) {
      const res = {
        data: response ? response.data : null,
        status: response ? response.status : null,
      };
      return Promise.reject(res);
    }
  }

  async detachUnsubscriptions(organizationId, id, type) {
    try {
      const requestUrl = `${this.versionPrefix}/organizations/${organizationId}/mails/templates/${id}/unsubscriptions`;
      await this.API.delete(requestUrl, {
        data: {
          data: {
            type: 'mail_template_category',
            id,
            attributes: {
              channel: type,
            },
          },
        },
      });
      return Promise.resolve();
    } catch ({ response, code }) {
      const res = {
        data: response ? response.data : null,
        status: response ? response.status : null,
      };
      return Promise.reject(res);
    }
  }

  async attachUnsubscriptions(organizationId, id, type) {
    try {
      const requestUrl = `${this.versionPrefix}/organizations/${organizationId}/mails/templates/${id}/unsubscriptions`;
      await this.API.post(requestUrl, {
        data: {
          type: 'mail_template_category',
          id,
          attributes: {
            channel: type,
          },
        },
      });
      return Promise.resolve();
    } catch ({ response, code }) {
      const res = {
        data: response ? response.data : null,
        status: response ? response.status : null,
      };
      return Promise.reject(res);
    }
  }

  async deleteOrganizationAddress(organizationId, id) {
    const requestURI = `${this.url}${organizationId}/addresses/${id}`;
    try {
      const response = await this.API.delete(requestURI);
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async createOrganizationAddress(organizationId, attributes) {
    const requestURI = `${this.url}${organizationId}/addresses`;
    try {
      const response = await this.API.post(requestURI, {
        data: {
          type: 'address',
          attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateOrganizationAddress(organizationId, id, attributes) {
    const requestURI = `${this.url}${organizationId}/addresses/${id}`;
    try {
      const response = await this.API.patch(requestURI, {
        data: {
          id,
          type: 'address',
          attributes,
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get settings for a particular organization
   * @param organizationId
   * @returns {Promise<unknown>}
   */
  async getOrganizationSettings(organizationId) {
    const requestURI = `${this.url}${organizationId}/settings`;
    try {
      const response = await this.API.get(requestURI);
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Update an array of organization settings
   * @param organizationId - the organization Uuid
   * @param payload - Array of organization settings
   * @returns {Promise<unknown>}
   */
  async updateOrganizationSettings(organizationId, payload) {
    // organizations/f22a3dc8-ff32-11ea-9c40-06cd5087dcc2/settings
    const requestURI = `${this.url}${organizationId}/settings`;
    try {
      const response = await this.API.patch(requestURI, {
        data: payload,
      });
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * get organization identification types
   * @param organizationId
   * @returns {Promise<unknown>}
   */
  async getOrganizationIdentificationTypes(organizationId) {
    const requestURI = `${this.url}${organizationId}/identifications/types`;
    try {
      const response = await this.API.get(requestURI);
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  /**
   * Get currencys from the API
   * @returns {Promise<unknown>}
   */
  async getCurrencys() {
    const requestURI = `${this.versionPrefix}/currencies`;
    try {
      const response = await this.API.get(requestURI);
      return Promise.resolve(response?.data?.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  async updateOrganizationCurrency(organizationId, currency) {
    const requestURI = `${this.url}${organizationId}/payments/info`;
    try {
      const response = await this.API.patch(requestURI, {
        data: {
          type: 'organization_payment_info',
          attributes: {
            currency,
          },
        },
      });
      return Promise.resolve(response.data);
    } catch (error) {
      return Promise.reject(error);
    }
  }
}

const OrganizationsAPI = new _OrganizationsAPI();
export default OrganizationsAPI;
